import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDictionaryStore } from '../../../hooks/useDictionaryStore';
import { detectLanguage } from '../../common/utils';
import { getSolrData } from '../../../hooks/useSolr';
import { useInfiniteQuery } from 'react-query';
import MandalaSkeleton from '../../common/MandalaSkeleton';
import { TermsGlossaryEntry } from './TermsGlossaryEntry';

export default function TermsGlossarySearch() {
    const { dictionaries, searchTerm } = useDictionaryStore();

    const entriesPerPage = 100;

    const queryfield =
        detectLanguage(searchTerm) === 'sa' ? 'name_deva' : `header`;

    const seldicts = Object.keys(
        Object.fromEntries(
            Object.entries(dictionaries).filter(([_, v]) => v.selected)
        )
    ).join(' OR ');

    // console.log('Selected Dictionaries', seldicts);

    const myfq = [
        `tree:terms`,
        '-associated_subject_ids:10524',
        'ancestors_new.alpha:*',
    ];
    if (seldicts?.length > 0) {
        myfq.push(
            `{!parent which=block_type:parent}related_definitions_source_code_s:(${seldicts})`
        );
    }
    const getSearchResults = async ({ pageParam }) => {
        // console.log('*** pg in getsuggestions', pageParam);
        if (!pageParam) {
            pageParam = 0;
        }
        const glossarySearchQuery = {
            index: 'terms',
            params: {
                q: `${queryfield}:("${searchTerm}" OR ${searchTerm}*)`,
                fq: myfq,
                start: pageParam * entriesPerPage,
                rows: entriesPerPage,
                fl: '*,[child parentFilter=block_type:parent childFilter=-block_child_type:places_shape limit=1000]',
                facet: false,
                sort: `score DESC, cascading_position_i ASC`,
            },
        };
        //return getSolrData(glossarySearchQuery, false);
        const resp = await getSolrData(glossarySearchQuery, false);
        return {
            numFound: resp?.numFound,
            data: resp?.docs,
            nextPage:
                resp?.docs?.length === entriesPerPage
                    ? pageParam + 1
                    : undefined,
        };
    };

    const {
        data: searchData,
        error: searchError,
        fetchNextPage, // a comment here
        hasNextPage,
        isFetching,
        isFetchingNextPage,
        status,
    } = useInfiniteQuery({
        queryKey: ['search-term', searchTerm],
        queryFn: getSearchResults,
        initialPageParam: 0,
        getNextPageParam: (lastPage, pages) => {
            // console.log("lastPage", lastPage);
            return lastPage.nextPage;
            // console.log('last page in get next', lastPage, pages);
            // console.log('returning', lastPage.start / 100 + 1);
            //return lastPage.start / 100 + 1;
        },
    });

    // Infinite scrol ref and function
    const fetchingRef = useRef(false);
    const observer = useRef();
    const lastItemRef = useCallback(
        (node) => {
            if (!node) return;

            if (observer.current) observer.current.disconnect();

            observer.current = new IntersectionObserver((entries) => {
                //console.log('entries', entries.entries);
                if (
                    entries[0].isIntersecting &&
                    hasNextPage &&
                    !fetchingRef.current
                ) {
                    // console.log("Fetched next page");
                    fetchingRef.current = true;
                    fetchNextPage();
                }
            });
            if (node) {
                // console.log('setting observer');
                observer.current.observe(node);
            }
        },
        [fetchNextPage, hasNextPage]
    );

    useEffect(() => {
        if (!isFetchingNextPage && !isFetching) {
            fetchingRef.current = false; // 🔓 unlock after fetch completes
        }
    }, [isFetchingNextPage]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [searchTerm]);

    if (searchError) {
        console.error(searchError);
        return (
            <div>
                <h1>Error</h1>
                <p>There was a error, Will Robinson!</p>
            </div>
        );
    }

    if (isFetching || isFetchingNextPage || !searchData) {
        return <MandalaSkeleton />;
    }
    // console.log('Search Results', searchData);
    /*
    const resultList = searchData?.pages[0]?.docs?.map((doc, di) => (
        <TermsGlossaryEntry data={doc} key={`gloss-entry-${doc.id}`} />
    ));

 */

    // Filter out the Terms grouping letters so they don't display
    const pages = searchData?.pages.map((pg, pid) => {
        const pgidat = pid === searchData.pages.length - 1 ? 'last' : pid;
        return (
            <div key={`gloss-pagekey-${pid}`} id={`gloss-page-${pgidat}`}>
                {pg.data
                    .filter((ent) => ent.associated_subjects[0] !== 'Letter')
                    .map((ent, ei) => (
                        <TermsGlossaryEntry
                            data={ent}
                            key={`gloss-entry-${ent.id}`}
                        />
                    ))}
            </div>
        );
    });

    const pagesLength =
        searchData && searchData?.pages ? searchData.pages.length : 0;
    const hasResults = pagesLength > 0 && searchData.pages[0].data?.length > 0;

    return (
        <div id="termGlossarySearch">
            <h1>Search Results for “{searchTerm}”</h1>
            <h2>Found {searchData.pages[0].numFound} matches.</h2>
            {hasResults && (
                <>
                    <div style={{ minHeight: '100vh' }}>{pages}</div>
                    {hasNextPage && !isFetchingNextPage && (
                        <div ref={lastItemRef}>Loading...</div>
                    )}
                </>
            )}
            {pages?.length === 0 && (
                <div>No matches found for {searchTerm}!</div>
            )}
        </div>
    );
}
